import { useState, useCallback } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { AlignLeft, AlignCenter, AlignRight, Maximize, Trash2 } from 'lucide-react';

const AlignableYoutubeComponent = ({ node, updateAttributes, selected, deleteNode }) => {
  const { src, width, height, align } = node.attrs;

  const wrapperStyle = {
    left: { float: 'left', margin: '0 1.5rem 1rem 0', clear: 'left' },
    right: { float: 'right', margin: '0 0 1rem 1.5rem', clear: 'right' },
    center: { display: 'flex', justifyContent: 'center', margin: '1.5rem 0', clear: 'both' },
    full: { display: 'block', margin: '1.5rem 0', clear: 'both' },
  }[align] || {};

  const [resizing, setResizing] = useState(false);
  const startRef = { current: null };

  const onMouseDown = useCallback((e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = width || 640;
    const ratio = (height || 360) / startW;

    const onMouseMove = (ev) => {
      const dx = corner.includes('e') ? ev.clientX - startX : startX - ev.clientX;
      const newW = Math.max(200, Math.round(startW + dx));
      const newH = Math.round(newW * ratio);
      updateAttributes({ width: newW, height: newH });
    };

    const onMouseUp = () => {
      setResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    setResizing(true);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [width, height, updateAttributes]);

  // Extract YouTube video ID from src
  const getEmbedUrl = () => {
    if (!src) return '';
    if (src.includes('youtube.com/embed/')) return src;
    const match = src.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : src;
  };

  return (
    <NodeViewWrapper
      style={wrapperStyle}
      data-align={align}
      className={`relative group ${selected ? 'ring-2 ring-blue-400 ring-offset-2 rounded' : ''}`}
    >
      <div className="relative inline-block" style={{ width: width || 640 }}>
        <iframe
          src={getEmbedUrl()}
          width={width || 640}
          height={height || 360}
          frameBorder="0"
          allowFullScreen
          style={{ maxWidth: '100%', borderRadius: '4px' }}
          data-testid="editor-youtube"
        />

        {selected && (
          <>
            <div
              className="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-stone-900 text-white rounded-lg px-2 py-1.5 shadow-xl z-50"
              contentEditable={false}
            >
              <button
                onClick={() => updateAttributes({ align: 'left' })}
                className={`p-1.5 rounded hover:bg-stone-700 ${align === 'left' ? 'bg-stone-700' : ''}`}
                title="Float left"
              >
                <AlignLeft size={15} />
              </button>
              <button
                onClick={() => updateAttributes({ align: 'center' })}
                className={`p-1.5 rounded hover:bg-stone-700 ${align === 'center' ? 'bg-stone-700' : ''}`}
                title="Center"
              >
                <AlignCenter size={15} />
              </button>
              <button
                onClick={() => updateAttributes({ align: 'right' })}
                className={`p-1.5 rounded hover:bg-stone-700 ${align === 'right' ? 'bg-stone-700' : ''}`}
                title="Float right"
              >
                <AlignRight size={15} />
              </button>
              <button
                onClick={() => updateAttributes({ align: 'full' })}
                className={`p-1.5 rounded hover:bg-stone-700 ${align === 'full' ? 'bg-stone-700' : ''}`}
                title="Full width"
              >
                <Maximize size={15} />
              </button>
              <div className="w-px h-5 bg-stone-600 mx-1" />
              <button
                onClick={deleteNode}
                className="p-1.5 rounded hover:bg-red-700"
                title="Delete video"
              >
                <Trash2 size={15} />
              </button>
            </div>

            {/* Resize handles */}
            {['ne', 'se'].map((corner) => (
              <div
                key={corner}
                onMouseDown={(e) => onMouseDown(e, corner)}
                className="absolute w-3 h-3 bg-blue-500 border-2 border-white rounded-full shadow cursor-nesw-resize z-40"
                style={{
                  top: corner === 'ne' ? -6 : 'auto',
                  bottom: corner === 'se' ? -6 : 'auto',
                  right: -6,
                }}
              />
            ))}
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export default AlignableYoutubeComponent;
